import { FC, forwardRef, ReactNode } from "react";

import Box, { BoxProps } from "@mui/material/Box";
import { Theme, useTheme, darken, SxProps } from "@mui/material/styles";

import { DashboardIcon, DashboardIconProps } from "@/dashboard-design-system/Icon";
import { DashboardSkeleton, DashboardSkeletonProps } from "@/dashboard-design-system/Skeleton";
import { DashboardTypography } from "@/dashboard-design-system/Typography";
import { mergeSx } from "@/helpers/mergeSx";
import { Cross as Close } from "@/icons/outlined";
import type { IconElement } from "@/icons/types";
import { ZeffyPaletteVibe } from "@/types/mui";

export const chipDeleteButtonDataTest = "chip-close-button";

export type DashboardChipVibe = Extract<
  ZeffyPaletteVibe,
  "brand" | "neutral" | "danger" | "warning" | "positive" | "form"
>;

export type DashboardChipVariant = "filled" | "outlined";

export type DashboardChipShape = "pill" | "rounded";

export type DashboardChipProps = Omit<BoxProps, "ref" | "children" | "onClick"> & {
  icon?: IconElement;
  variant?: DashboardChipVariant;
  shape?: DashboardChipShape;
  vibe?: DashboardChipVibe;
  label: ReactNode;
  disabled?: boolean;
  onClick?: (e: React.MouseEvent<HTMLDivElement>) => void;
  onDelete?: (e: React.MouseEvent<HTMLDivElement>) => void;
  slotProps?: {
    icon?: {
      vibe?: DashboardIconProps["vibe"];
      size?: DashboardIconProps["size"];
      sx?: SxProps<Theme>;
    };
    label?: {
      sx?: SxProps<Theme>;
    };
    deleteIcon?: {
      sx?: SxProps<Theme>;
    };
  };
};

const getFilledSx = (theme: Theme, vibe: DashboardChipVibe, shape: DashboardChipShape, isInteractive: boolean) => {
  const { backgroundColor, color, hoverBackgroundColor } = (() => {
    if (shape === "rounded") {
      return {
        backgroundColor: theme.palette.surface[vibe].quiet,
        color: theme.palette.text[vibe].intense,
        hoverBackgroundColor: theme.palette.surface[vibe].moderate,
      };
    }

    const backgroundColor = vibe === "form" ? theme.palette.surface.form.intense : theme.palette.surface[vibe].intense;

    return {
      backgroundColor,
      color: theme.palette.text.reverse.intense,
      hoverBackgroundColor: darken(backgroundColor, 0.3),
    };
  })();

  return {
    backgroundColor,
    color,
    ...(isInteractive && {
      "&:hover": {
        backgroundColor: hoverBackgroundColor,
      },
    }),
  };
};

const getOutlinedSx = (theme: Theme, vibe: DashboardChipVibe, isInteractive: boolean) => {
  const { backgroundColor, color, borderColor, hoverBackgroundColor } = (() => {
    return {
      color: theme.palette.text[vibe].intense,
      backgroundColor: theme.palette.surface[vibe].quiet,
      borderColor: vibe === "form" ? theme.palette.border.form.moderate : theme.palette.border[vibe].moderate,
      hoverBackgroundColor: theme.palette.surface[vibe].moderate,
    };
  })();

  return {
    color,
    backgroundColor,
    border: `1px solid ${borderColor}`,
    ...(isInteractive && {
      "&:hover": {
        backgroundColor: hoverBackgroundColor,
      },
    }),
  };
};

export const DashboardChip = forwardRef<HTMLDivElement, DashboardChipProps>(
  (
    {
      shape = "pill",
      vibe = "neutral",
      variant = "filled",
      disabled = false,
      icon,
      label,
      onClick,
      onDelete,
      slotProps,
      sx,
      ...rest
    },
    ref
  ) => {
    const theme = useTheme();
    const isInteractive = (!!onClick || !!onDelete) && !disabled;

    const styles = mergeSx(
      {
        display: "inline-flex",
        alignItems: "center",
        paddingInline: 1,
        gap: theme.spacing(0.5),
        whiteSpace: "nowrap",
        ...(isInteractive && {
          border: "none",
          cursor: "pointer",
          transition: "background-color 200ms linear, color 200ms linear, border 200ms linear, outline 100ms ease-out",
          outline: "0px solid transparent",
          ["&:focus-visible"]: {
            outline: `3px solid ${theme.palette.border.brand.moderate}`,
          },
        }),
        ...(variant === "filled" && getFilledSx(theme, vibe, shape, isInteractive)),
        ...(variant === "outlined" && getOutlinedSx(theme, vibe, isInteractive)),
        ...(shape === "rounded" && {
          height: 24,
          borderRadius: 0.5,
        }),
        ...(shape === "pill" && {
          height: 20,
          borderRadius: 9999,
        }),
        ...(disabled && {
          opacity: 0.5,
        }),
      },
      sx
    );

    return (
      <Box
        ref={ref}
        onClick={onClick ?? onDelete}
        sx={styles}
        component={isInteractive ? "button" : "div"}
        disabled={disabled}
        data-test-component-name="dashboard-chip"
        {...rest}
      >
        {icon && (
          <DashboardIcon
            vibe="currentColor"
            size="extraSmall"
            {...(slotProps?.icon ?? {})}
            sx={mergeSx({ flexShrink: 0 }, slotProps?.icon?.sx)}
          >
            {icon}
          </DashboardIcon>
        )}
        <DashboardTypography
          variant="caption"
          component="span"
          sx={mergeSx(
            {
              flex: "1 1 auto",
              minWidth: 0,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            },
            slotProps?.label?.sx
          )}
        >
          {label}
        </DashboardTypography>
        {onDelete && (
          <DashboardIcon
            size="extraSmall"
            vibe="currentColor"
            onClick={isInteractive ? onDelete : undefined}
            sx={mergeSx({ flexShrink: 0 }, slotProps?.deleteIcon?.sx)}
          >
            <Close />
          </DashboardIcon>
        )}
      </Box>
    );
  }
);

DashboardChip.displayName = "DashboardChip";

type DashboardChipSkeletonProps = {
  shape?: DashboardChipShape;
  width?: DashboardSkeletonProps["width"];
};

export const DashboardChipSkeleton: FC<DashboardChipSkeletonProps> = ({ width = "50px", shape = "pill" }) => {
  return (
    <DashboardSkeleton
      variant="rectangular"
      width={width}
      height={shape === "pill" ? "20px" : "24px"}
      sx={{ borderRadius: shape === "pill" ? 9999 : 0.5 }}
    />
  );
};
